import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ResponsiveDialog from '../components/common/ResponsiveDialog';
import ResponsiveTable, { ResponsiveTableColumn } from '../components/common/ResponsiveTable';
import apiService from '../services/api.service';
import type { GLRuleCopyPreview, GLRuleCopyRun, GLRuleYear } from '../types/itemRequests';

/**
 * Settings > G/L Rules Year Copy (Admin): copies SAP's advanced G/L account
 * determination rules of one financial year into another, for the selected
 * company. The target year must be opened in SAP first (its posting periods).
 * Rules the target year already has are skipped, so a copy can be run again.
 */

const formatDate = (value: string | null | undefined): string =>
  value ? new Date(value).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '';
const errorMessage = (e: any, fallback: string) => e?.response?.data?.message || e?.message || fallback;

const RUN_STATUS: Record<GLRuleCopyRun['status'], { label: string; color: 'info' | 'success' | 'warning' | 'error' }> = {
  Running: { label: 'Running', color: 'info' },
  Completed: { label: 'Completed', color: 'success' },
  CompletedWithErrors: { label: 'Completed with errors', color: 'warning' },
  Failed: { label: 'Failed', color: 'error' },
};

const GLRuleYearCopy: React.FC = () => {
  const companyId = useMemo(() => Number(localStorage.getItem('selectedCompanyId') || '') || 0, []);
  const [years, setYears] = useState<GLRuleYear[] | null>(null);
  const [runs, setRuns] = useState<GLRuleCopyRun[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [fromPeriod, setFromPeriod] = useState('');
  const [toPeriod, setToPeriod] = useState('');
  const [preview, setPreview] = useState<GLRuleCopyPreview | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [showRules, setShowRules] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [run, setRun] = useState<GLRuleCopyRun | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const poll = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadYears = useCallback(async () => {
    try {
      const [y, r]: any[] = await Promise.all([apiService.getGlRuleYears(companyId), apiService.listGlRuleCopyRuns(companyId)]);
      const list: GLRuleYear[] = y.data || [];
      setYears(list);
      setRuns(r.data || []);
      return list;
    } catch (e: any) {
      setError(errorMessage(e, 'The financial years could not be read from SAP'));
      return [] as GLRuleYear[];
    }
  }, [companyId]);

  useEffect(() => {
    if (!companyId) { setError('Select a company first.'); return undefined; }
    loadYears().then((list) => {
      const withRules = list.filter((y) => (y.rules || 0) > 0);
      const from = withRules.length ? withRules[withRules.length - 1] : list[list.length - 1];
      if (from) {
        setFromPeriod(from.code);
        const next = list.find((y) => y.begin > from.begin);
        setToPeriod(next ? next.code : '');
      }
    });
    return () => { if (poll.current) clearTimeout(poll.current); };
  }, [companyId, loadYears]);

  useEffect(() => { setPreview(null); setPreviewError(null); }, [fromPeriod, toPeriod]);

  // Rules are copied forward only: the target is a financial year that begins after the source.
  const targets = useMemo(() => {
    const from = (years || []).find((y) => y.code === fromPeriod);
    return from ? (years || []).filter((y) => y.begin > from.begin) : [];
  }, [years, fromPeriod]);
  useEffect(() => {
    if (toPeriod && !targets.some((y) => y.code === toPeriod)) setToPeriod(targets[0]?.code || '');
  }, [targets, toPeriod]);

  const runPreview = async () => {
    setPreviewBusy(true);
    setPreviewError(null);
    try {
      const res: any = await apiService.previewGlRuleCopy({ companyId, fromPeriod, toPeriod });
      setPreview(res.data);
    } catch (e: any) {
      setPreviewError(errorMessage(e, 'The copy could not be previewed'));
    } finally {
      setPreviewBusy(false);
    }
  };

  const follow = useCallback((runId: number) => {
    const tick = async () => {
      try {
        const res: any = await apiService.getGlRuleCopyRun(runId);
        setRun(res.data);
        if (res.data.status === 'Running') { poll.current = setTimeout(tick, 2000); return; }
        await loadYears();
      } catch {
        poll.current = setTimeout(tick, 4000);
      }
    };
    tick();
  }, [loadYears]);

  const start = async () => {
    setConfirming(false);
    setStartError(null);
    try {
      const res: any = await apiService.startGlRuleCopy({ companyId, fromPeriod, toPeriod });
      setPreview(null);
      follow(Number(res.data.runId));
    } catch (e: any) {
      setStartError(errorMessage(e, 'The copy could not be started'));
    }
  };

  if (error) return <Box sx={{ p: { xs: 2, md: 3 } }}><Alert severity="error">{error}</Alert></Box>;
  if (!years) return <Stack direction="row" spacing={1.5} sx={{ p: 3 }} role="status"><CircularProgress size={22} /><Typography>Reading the financial years from SAP…</Typography></Stack>;

  const latest = years[years.length - 1];
  const failures = (run?.results || []).filter((r) => r.status === 'failed');
  const ruleColumns: ResponsiveTableColumn<GLRuleCopyPreview['toCopy'][number]>[] = [
    { id: 'code', header: 'Rule', cell: (r) => `${r.code}${r.description ? ` · ${r.description}` : ''}` },
    { id: 'class', header: 'Classification', cell: (r) => r.classification },
    { id: 'type', header: 'Warehouse type', cell: (r) => r.warehouseType },
    { id: 'scope', header: 'Only for', cell: (r) => r.scope, hideOnMobile: true },
  ];
  const runColumns: ResponsiveTableColumn<GLRuleCopyRun>[] = [
    { id: 'when', header: 'Started', cell: (r) => formatDate(r.starteddate) },
    { id: 'years', header: 'Years', cell: (r) => `${r.fromperiod} → ${r.toperiod}` },
    { id: 'status', header: 'Status', cell: (r) => <Chip size="small" color={RUN_STATUS[r.status].color} label={RUN_STATUS[r.status].label} /> },
    { id: 'counts', header: 'Copied / skipped / failed', cell: (r) => `${r.copied} / ${r.skipped} / ${r.failed}` },
    { id: 'by', header: 'By', cell: (r) => r.startedbyname || '', hideOnMobile: true },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, display: 'grid', gap: 2.5, maxWidth: 1100 }}>
      <Box>
        <Typography variant="h5" component="h1">G/L Rules Year Copy</Typography>
        <Typography variant="body2" color="text.secondary">
          SAP keeps one set of Advanced G/L Account Determination rules per financial year. Copy a year’s rules into the next one after opening that year in SAP.
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 } }}>
        <Typography variant="h6" component="h2" sx={{ mb: 1.5 }}>Financial years in SAP</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {years.map((y) => (
            <Chip key={y.code} variant="outlined" label={`${y.code} · ${y.rules ?? '?'} rules`} />
          ))}
        </Stack>
        {latest && !years.some((y) => y.begin > latest.begin) && (
          <Alert severity="info" sx={{ mt: 2 }}>
            SAP has no financial year after {latest.code} yet. Create its posting periods in SAP (Administration › System Initialization › Posting Periods), then come back to copy the rules.
          </Alert>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, display: 'grid', gap: 2 }}>
        <Typography variant="h6" component="h2">Copy</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField select size="small" label="Copy the rules of" value={fromPeriod} onChange={(e) => setFromPeriod(e.target.value)} sx={{ minWidth: 200 }}>
            {years.map((y) => <MenuItem key={y.code} value={y.code}>{y.code} ({y.rules ?? '?'} rules)</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Into" value={toPeriod} onChange={(e) => setToPeriod(e.target.value)} sx={{ minWidth: 200 }}
            disabled={!targets.length} helperText={fromPeriod && !targets.length ? `SAP has no financial year after ${fromPeriod}` : undefined}>
            {targets.map((y) => <MenuItem key={y.code} value={y.code}>{y.code} ({y.rules ?? '?'} rules)</MenuItem>)}
          </TextField>
          <Button variant="outlined" onClick={runPreview} disabled={!fromPeriod || !toPeriod || previewBusy || run?.status === 'Running'} sx={{ alignSelf: 'flex-start' }}>
            {previewBusy ? 'Checking…' : 'Preview'}
          </Button>
        </Stack>
        {previewError && <Alert severity="error">{previewError}</Alert>}
        {preview && (
          <Stack spacing={1.5}>
            <Alert severity={preview.toCopyCount ? 'info' : 'success'}>
              {preview.fromPeriod} has {preview.sourceCount} rules. {preview.alreadyCount} of them are already in {preview.toPeriod};
              {' '}{preview.toCopyCount ? `${preview.toCopyCount} will be copied.` : 'nothing is left to copy.'}
            </Alert>
            {preview.byClassification.length > 0 && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {preview.byClassification.map((c) => <Chip key={c.classification} size="small" label={`${c.classification}: ${c.count}`} />)}
              </Stack>
            )}
            {preview.toCopyCount > 0 && (
              <Box>
                <Button size="small" onClick={() => setShowRules((s) => !s)}>{showRules ? 'Hide the rules' : `Show the ${preview.toCopyCount} rules`}</Button>
                {showRules && (
                  <Box sx={{ mt: 1 }}>
                    <ResponsiveTable ariaLabel="Rules to copy" columns={ruleColumns} rows={preview.toCopy} getRowKey={(r) => r.absoluteEntry} />
                  </Box>
                )}
              </Box>
            )}
            {preview.toCopyCount > 0 && (
              <Box><Button variant="contained" onClick={() => setConfirming(true)}>Copy {preview.toCopyCount} rules into {preview.toPeriod}</Button></Box>
            )}
          </Stack>
        )}
        {startError && <Alert severity="error">{startError}</Alert>}
        {run && (
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle2">Copy {run.fromperiod} → {run.toperiod}</Typography>
              <Chip size="small" color={RUN_STATUS[run.status].color} label={RUN_STATUS[run.status].label} />
            </Stack>
            {run.status === 'Running' && <LinearProgress variant={run.total ? 'determinate' : 'indeterminate'} value={run.total ? ((run.copied + run.skipped + run.failed) / run.total) * 100 : 0} />}
            <Typography variant="body2">{run.copied} copied, {run.skipped} already there, {run.failed} refused{run.total ? ` of ${run.total}` : ''}.</Typography>
            {run.error && <Alert severity="error">{run.error}</Alert>}
            {failures.length > 0 && (
              <Alert severity="warning">
                SAP refused {failures.length} rule{failures.length === 1 ? '' : 's'}:{' '}
                {failures.slice(0, 10).map((f) => `${f.code}${f.description ? ` ${f.description}` : ''} (${f.error})`).join('; ')}
                {failures.length > 10 ? ` and ${failures.length - 10} more` : ''}
              </Alert>
            )}
          </Stack>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 } }}>
        <Typography variant="h6" component="h2" sx={{ mb: 1.5 }}>Earlier copies</Typography>
        <ResponsiveTable ariaLabel="Earlier copies" columns={runColumns} rows={runs} getRowKey={(r) => r.runid} emptyMessage="No copy has been run for this company yet." />
      </Paper>

      <ResponsiveDialog open={confirming} onClose={() => setConfirming(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Copy {preview?.toCopyCount} rules into {preview?.toPeriod}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            PRPO creates each rule of {preview?.fromPeriod} that {preview?.toPeriod} does not have yet, with the same conditions and accounts. Rules already in {preview?.toPeriod} are not changed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)}>Back</Button>
          <Button variant="contained" onClick={start}>Copy the rules</Button>
        </DialogActions>
      </ResponsiveDialog>
    </Box>
  );
};

export default GLRuleYearCopy;
